import type { AggregationOperator } from '@vy/types';
import { AGGREGATION_OPERATORS } from '@vy/types';
import { isNullOrUndefined, isInThePast, isNumber, vyDate } from '@vy/utils';
import type { ChartData, ChartDataset, DefaultDataPoint } from 'chart.js';
import { useMemo } from 'react';

const TEMPORAL_AGGREGATION_OPERATORS: AggregationOperator[] = [
  AGGREGATION_OPERATORS.Max,
  AGGREGATION_OPERATORS.Min,
  AGGREGATION_OPERATORS.First,
];

export function useChartUtils(aggregationOperator?: AggregationOperator, treatLastAsDaily = false) {
  const shouldShowLineChart = useMemo(() => {
    if (isNullOrUndefined(aggregationOperator)) {
      return false;
    }

    if (aggregationOperator === AGGREGATION_OPERATORS.Last) {
      return !treatLastAsDaily;
    }

    return TEMPORAL_AGGREGATION_OPERATORS.includes(aggregationOperator);
  }, [aggregationOperator, treatLastAsDaily]);

  const filterLabelsInThePast = (labels: string[]) => {
    return labels.filter((label) => isInThePast(vyDate(label, { format: 'DD/MM' }).toDate()));
  };

  const sliceDatasetDataByLength = (
    datasets: ChartDataset<'line', DefaultDataPoint<'line'>>[],
    length: number,
  ) => {
    return datasets.map((dataset: ChartDataset<'line', DefaultDataPoint<'line'>>) => ({
      ...dataset,
      data: dataset.data.filter(isNumber).slice(0, length),
    }));
  };

  const filterPastChartData = (
    chartLabels: string[],
    chartData: ChartData<'line', DefaultDataPoint<'line'>, string>,
  ): ChartData<'line', number[], string> => {
    const pastChartLabels = filterLabelsInThePast(chartLabels);

    return {
      ...chartData,
      labels: pastChartLabels,
      datasets: sliceDatasetDataByLength(chartData.datasets, pastChartLabels.length),
    };
  };

  return { shouldShowLineChart, filterPastChartData };
}
